import type React from "react";

import { useUser } from "@/contexts/user-provider";

import type { CalendarTextSize } from "@/types/provider";

/**
 * How much each Calendar text size scales event text. The calendar-* font
 * sizes in tailwind.config multiply by --calendar-font-scale, so every event
 * card inside an element carrying {@link calendarTextScaleStyle} follows it.
 */
export const CALENDAR_TEXT_SCALE: Record<CalendarTextSize, number> = {
  // Small is the calendar's original look, and where everyone starts.
  small: 1,
  medium: 1.3,
  large: 1.65,
  extra_large: 2,
};

export const CALENDAR_TEXT_SIZE_OPTIONS: Array<{
  value: CalendarTextSize;
  label: string;
}> = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
  { value: "extra_large", label: "Extra large" },
];

/**
 * A segment's title and time share one line until the segment is tall enough
 * for both stacked: a title line, a time line and the gap, which grows with
 * the text. Container queries cannot read CSS variables, so each size spells
 * out its own height (23px at Small). The class strings must stay
 * literal for Tailwind to generate them.
 */
const SEGMENT_HEADER_CLASSNAME: Record<CalendarTextSize, string> = {
  small:
    "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:23px)]:flex-col [@container_(min-height:23px)]:flex-nowrap [@container_(min-height:23px)]:items-start [@container_(min-height:23px)]:gap-px",
  medium:
    "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:30px)]:flex-col [@container_(min-height:30px)]:flex-nowrap [@container_(min-height:30px)]:items-start [@container_(min-height:30px)]:gap-px",
  large:
    "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:38px)]:flex-col [@container_(min-height:38px)]:flex-nowrap [@container_(min-height:38px)]:items-start [@container_(min-height:38px)]:gap-px",
  extra_large:
    "flex w-full flex-wrap items-center gap-0.5 [@container_(min-height:46px)]:flex-col [@container_(min-height:46px)]:flex-nowrap [@container_(min-height:46px)]:items-start [@container_(min-height:46px)]:gap-px",
};

/**
 * Memo gates, sized for each text size like the segment header above. A card
 * narrower than the width shows only the memo icon (41px at Small); a space
 * shorter than the height hides the memo text (13px at Small, one line).
 */
const MEMO_GATE_CLASSNAMES: Record<
  CalendarTextSize,
  { iconOnly: string; text: string; list: string }
> = {
  small: {
    iconOnly: "@[41px]:hidden",
    text: "hidden @[41px]:block",
    list: "[@container_(max-height:13px)]:hidden",
  },
  medium: {
    iconOnly: "@[53px]:hidden",
    text: "hidden @[53px]:block",
    list: "[@container_(max-height:17px)]:hidden",
  },
  large: {
    iconOnly: "@[68px]:hidden",
    text: "hidden @[68px]:block",
    list: "[@container_(max-height:21px)]:hidden",
  },
  extra_large: {
    iconOnly: "@[82px]:hidden",
    text: "hidden @[82px]:block",
    list: "[@container_(max-height:26px)]:hidden",
  },
};

export function memoGateClassNames(size: CalendarTextSize) {
  return MEMO_GATE_CLASSNAMES[size];
}

export function segmentHeaderClassName(size: CalendarTextSize): string {
  return SEGMENT_HEADER_CLASSNAME[size];
}

export function calendarTextScaleStyle(
  size: CalendarTextSize,
): React.CSSProperties {
  return {
    "--calendar-font-scale": CALENDAR_TEXT_SCALE[size],
  } as React.CSSProperties;
}

/** The signed-in user's Calendar text size. */
export function useCalendarTextSize(): CalendarTextSize {
  const { currentUser } = useUser();
  return currentUser?.calendarTextSize ?? "small";
}
